if (!customElements.get("color-dropdowns")) {
  customElements.define(
    "color-dropdowns",
    class ColorDropdowns extends HTMLElement {
      variantNameChangedUnsubscriber = undefined;

      constructor() {
        super();

        this.colorDropdownContainers = Array.from(
          this.getElementsByClassName("color-dropdown-container")
        );

        this.colorDropdownContainers.forEach(cdc => {
          const selectedColor = cdc.querySelector(".selected-color");
          const colorsContainers = cdc.querySelectorAll(".colors-container");

          // Toggle color dropdown open/closed
          selectedColor.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            colorsContainers.forEach(csc => {
              if (csc.dataset.colorsetName === selectedColor.dataset.currentColorset) {
                csc.classList.toggle("hidden");
              }
            });
          });

          // Color button selection
          colorsContainers.forEach(csc => {
            csc.getElementsByClassName("color-button") &&
            Array.from(csc.getElementsByClassName("color-button")).forEach(color => {
              color.addEventListener("click", () => {
                selectedColor.querySelector("p").innerHTML = color.dataset.colorName;
                selectedColor.querySelector("input").value = color.dataset.colorName;
                selectedColor.querySelector("img").src = color.dataset.imageSrc;
                selectedColor.style.outline = ""; // Clear any validation error
                csc.classList.add("hidden");
              });
            });
          });

          // Colorset switcher
          const colorsetOptionsContainer = cdc.querySelector(".colorset-options-container");
          if (colorsetOptionsContainer) {
            const colorsetOptions = Array.from(
              colorsetOptionsContainer.querySelectorAll(".colorset-option")
            );

            this._setColorsetBorder(colorsetOptions[0], true);

            colorsetOptions.forEach(cso => {
              cso.addEventListener("click", () => {
                selectedColor.dataset.currentColorset = cso.dataset.colorsetName;

                colorsetOptions.forEach(opt => {
                  this._setColorsetBorder(opt, opt === cso);
                });

                colorsContainers.forEach(csc => {
                  csc.classList.add("hidden");
                  if (csc.dataset.colorsetName === cso.dataset.colorsetName) {
                    selectedColor.querySelector("p").innerHTML = "Please select a color";
                    selectedColor.querySelector("input").value = "";
                    selectedColor.querySelector("img").src =
                      "https://cdn.shopify.com/s/files/1/0631/2889/5599/files/White.png?v=1743806970";
                  }
                });
              });
            });
          }
        });

        this._initStoredValues();
        //this.applyInitialVisibility();
        this._attachFormValidation();
      }

      // ─── Helpers ────────────────────────────────────────────────────────────

      _setColorsetBorder(el, active) {
        el.style.setProperty("--border-width", active ? "2px" : "1px");
        el.style.setProperty("--border-color", active ? "black" : "rgba(0, 0, 0, 0.26)");
      }

      _hideContainer(cdc) {
        const input = cdc.querySelector("input");
        input.dataset.storedValue = input.value;
        input.dataset.storedColorset = input.dataset.storedColorset || "";
        input.value = "";
        input.name = "";
        cdc.classList.add("hidden");
      }

      _showContainer(cdc) {
        const input = cdc.querySelector("input");
        if (!input) return;
        
        // Only restore storedValue if the container was actually hidden
        if (cdc.classList.contains("hidden")) {
          input.value = input.dataset.storedValue || "";
          input.name = input.dataset.storedName;
        }
        // If it was already visible, leave the current value alone
        
        cdc.classList.remove("hidden");
      }

      _initStoredValues() {
        this.colorDropdownContainers.forEach(cdc => {
          const input = cdc.querySelector("input");
          if (input && input.dataset.storedValue === undefined) {
            input.dataset.storedValue = input.value || "";
          }
          if (cdc.classList.contains("hidden")) {
            input.name = "";
          } else {
            input.name = input.dataset.storedName;
          }
        });
      }

      _attachFormValidation() {
        const form = this.closest('form') || document.querySelector('form[action="/cart/add"]');
        if (!form) return;
      
        // Use capture: true to run BEFORE the theme's Ajax handler
        form.addEventListener("submit", (e) => {
          if (!this._validateColors()) {
            e.preventDefault();
            e.stopImmediatePropagation(); // Stop ALL other submit listeners too
          }
        }, { capture: true });
      
        // Also intercept the add-to-cart button click directly as a fallback
        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) {
          submitBtn.addEventListener("click", (e) => {
            if (!this._validateColors()) {
              e.preventDefault();
              e.stopImmediatePropagation();
            }
          }, { capture: true });
        }
      }

      _validateColors() {
        const emptyFields = this.colorDropdownContainers.filter(cdc => {
          if (cdc.classList.contains("hidden")) return false;
          const input = cdc.querySelector("input");
          return !input || !input.value.trim();
        });
      
        if (emptyFields.length > 0) {
          emptyFields.forEach(cdc => {
            const selectedColor = cdc.querySelector(".selected-color");
            if (selectedColor) selectedColor.style.outline = "2px solid red";
          });
          emptyFields[0].scrollIntoView({ behavior: "smooth", block: "center" });
          return false;
        }
        return true;
      }

      // ─── Lifecycle ──────────────────────────────────────────────────────────

      get sectionId() {
        return this.getAttribute("data-section-id");
      }

      applyInitialVisibility() {
        const variantData = JSON.parse(document.querySelector("script[data-selected-variant]").innerText);
        this.updateColorDropdownsOnVariantChange(variantData.name);
      }

      connectedCallback() {
        this.applyInitialVisibility();
        this.variantNameChangedUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            this.variantName = event.data.variant.name.toString();
            this.updateColorDropdownsOnVariantChange(this.variantName);
          }
        );
      }

      disconnectedCallback() {
        this.variantNameChangedUnsubscriber?.();
      }

      // ─── Variant Logic ──────────────────────────────────────────────────────

      updateColorDropdownsOnVariantChange(variantName) {
        const variantOptions = variantName.split(" / ");
        const isSolid =
          variantName.includes("Solid") &&
          !(!variantName.includes("Solid Color") && variantName.includes("Solid Fill Color"));
        const is3DOnly = variantName.includes("3D Only");
        const isSpecial = variantName.includes("Model Matched Pattern");
        const isSingleOutline = variantOptions.includes("Single Outline");
        const is3DChrome = variantName.includes("Chrome") && variantName.includes("3D");

        // Misc ───────────
        const isDomed = !variantName.split("-").at(-1).includes("Flat");
        const domedWord = document.querySelector(".product__title span[data-word=Domed]");
        if(!isDomed && domedWord != undefined){
          domedWord.classList.add("hidden");
          domedWord.classList.remove("word");
        } else if (isDomed && domedWord != undefined) {
          domedWord.classList.remove("hidden");
          domedWord.classList.add("word");
        }

        const flatButton = document.querySelector("fieldset label[title='Flat']");
        if(flatButton != undefined){
          flatButton.innerHTML = "Flat Vinyl";
        }
        // end Misc ───────

        this.colorDropdownContainers.forEach(cdc => {
          const location = cdc.dataset.colorLocation;

          if (isSpecial) {
            this._hideContainer(cdc);
            return;
          }

          if (location === "Color") {
            this._showContainer(cdc);
            return;
          }

          if (location === "3D Color") {
            is3DChrome ? this._hideContainer(cdc) : this._showContainer(cdc);
            return;
          }

          if (location === "Overlay Color") {
            is3DOnly ? this._hideContainer(cdc) : this._showContainer(cdc);
            return;
          }

          if (location === "Inner Outline Color") {
            if (isSingleOutline) {
              this._showContainer(cdc);
              cdc.querySelector("p").innerHTML = "Outline Color";
              cdc.querySelector("input").name = "properties[Outline Color]";
            } else if (isSolid) {
              this._hideContainer(cdc);
            } else {
              this._showContainer(cdc);
              //cdc.querySelector("input").value = "";
              cdc.querySelector("p").innerHTML = "Inner Outline Color";
              cdc.querySelector("input").name = "properties[Inner Outline Color]";
            }
            return;
          }

          if (location === "Outer Outline Color") {
            isSingleOutline || isSolid
              ? this._hideContainer(cdc)
              : this._showContainer(cdc);
            return;
          }

          // All other locations
          isSolid || is3DOnly ? this._hideContainer(cdc) : this._showContainer(cdc);
        });
      }
    }
  );
}



/*
if (!customElements.get("color-dropdowns")) {
  customElements.define(
    "color-dropdowns",
    class ColorDropdowns extends HTMLElement {
      updateColorDropdownsUnsubscriber = undefined;
      variantNameChangedUnsubscriber = undefined;
      currentSelectedColor = undefined;
      constructor() {
        super();
        
        // OVERALL CONTAINER
        this.colorDropdownContainers = Array.from(
          this.getElementsByClassName("color-dropdown-container")
        );

        this.colorDropdownContainers.forEach(cdc => {

          const selectedColor = cdc.querySelector(".selected-color");
          const colorsContainers = cdc.querySelectorAll(".colors-container");

          selectedColor.addEventListener("click", function(e) {
            e.preventDefault();
            e.stopPropagation();
            colorsContainers.forEach(csc => {
              if (csc.dataset.colorsetName == this.dataset.currentColorset) {
                if (!csc.classList.contains("hidden")) {
                  console.log(csc.dataset.colorsetName)
                  csc.classList.add("hidden")
                } else {
                  csc.classList.remove("hidden")
                }
              }
            })
          });

          colorsContainers.forEach(csc => {
            const colorButtons = Array.from(
              csc.getElementsByClassName("color-button")
            );
            
            // BUTTONS FOR SELECTING COLORS
            colorButtons.forEach(color => {
              color.addEventListener("click", function(e) {
                selectedColor.querySelector("p").innerHTML = color.dataset.colorName;
                selectedColor.querySelector("input").value = color.dataset.colorName;
                selectedColor.querySelector("img").src = color.dataset.imageSrc;
                csc.classList.add("hidden");
              })
            })
          })
          if (cdc.querySelector(".colorset-options-container")){
            const colorsetOptions = Array.from(
              cdc.querySelector(".colorset-options-container")
              .querySelectorAll(".colorset-option")
            );
            
            // BUTTONS FOR SELECTING COLOR SETS
            colorsetOptions[0].style.setProperty("--border-width", "2px")
            colorsetOptions[0].style.setProperty("--border-color", "black")
            
            colorsetOptions.forEach(cso => {
              cso.addEventListener("click", function(e) {
                selectedColor.dataset.currentColorset = cso.dataset.colorsetName
                
                colorsetOptions.forEach(colorSetOption => {
                  if (colorSetOption == cso) {
                    colorSetOption.style.setProperty("--border-width", "2px")
                    colorSetOption.style.setProperty("--border-color", "black")
                  } else {
                    colorSetOption.style.setProperty("--border-width", "1px")
                    colorSetOption.style.setProperty("--border-color", "rgba(0, 0, 0, 0.26)")
                  }
                })
                
                colorsContainers.forEach(csc => {
                  if (!csc.classList.contains("hidden")) {
                    csc.classList.add("hidden");
                  }
                  if (csc.dataset.colorsetName === cso.dataset.colorsetName) {
                    selectedColor.querySelector("p").innerHTML = "Please select a color";
                    selectedColor.querySelector("input").value = "";
                    selectedColor.querySelector("img").src = "https://cdn.shopify.com/s/files/1/0631/2889/5599/files/White.png?v=1743806970";
                    csc.classList.add("hidden");
                  }
                });
              })
            });
          }
        });

        this.applyInitialVisibility();
      }

      applyInitialVisibility() {
        const variantInput = document.querySelector('[name="id"]');
        if (!variantInput) return;

        const selectedOption =
          variantInput.selectedOptions?.[0] ||
          variantInput.querySelector('option:checked');

        if (selectedOption) {
          this.updateColorDropdownsOnVariantChange(selectedOption.textContent);
        }
      }

      get sectionId() {
        return this.getAttribute("data-section-id");
      }

      connectedCallback() {
        // Update variantName if variant is switched on product page
        this.applyInitialVisibility();
        this.variantNameChangedUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.variantChange,
          (event) => {
            this.variantName = event.data.variant.name.toString();
            console.log(this.variantName);
            this.updateColorDropdownsOnVariantChange(this.variantName);
          }
        );
        
        const initialVariant = document.querySelector('[name="id"]')?.selectedOptions?.[0];
        if (initialVariant) {
          this.updateColorDropdownsOnVariantChange(initialVariant.textContent);
        }
      }

      disconnectedCallback() {
        if (this.variantNameChangedUnsubscriber) {
          this.variantNameChangedUnsubscriber();
        }
      }

      updateColorDropdownsOnVariantChange(variantName) {
        console.log(variantName)
        const variantOptions = variantName.split(' / ');
        let isSolid = variantName.includes("Solid");
        if (!variantName.includes("Solid Color") && variantName.includes("Solid Fill Color")){isSolid = false;}
        const is3DOnly = variantName.includes("3D Only");
        const isSpecial = variantName.includes("Model Matched Pattern");
        console.log(`Is special: %c${isSpecial}`, "color: lightorange");
        const isSingleOutlineNotDouble = variantOptions.includes("Single Outline");
        const is3DChrome = variantName.includes("Chrome") && variantName.includes("3D");
        console.log(`Is chrome: %c${is3DChrome}`, "color: lightorange");

        this.colorDropdownContainers.forEach(cdc => {
          const location = cdc.dataset.colorLocation;

          if (isSpecial) {
            cdc.classList.add("hidden");
            cdc.querySelector("input").dataset.storedValue = cdc.querySelector("input").value;
            cdc.querySelector("input").value = "";
            return;
          } 

          if (location === "Color") {
            cdc.classList.remove("hidden");
            return;
          }

          if (location === "3D Color") {
            if (is3DChrome) {
              cdc.classList.add("hidden");
              cdc.querySelector("input").dataset.storedValue = cdc.querySelector("input").value;
              cdc.querySelector("input").value = ""
            } else {
              cdc.classList.remove("hidden");
              cdc.querySelector("input").value = cdc.querySelector("input").dataset.storedValue;
            }
            return;
          }

          // Overlay Color hidden ONLY for 3D Only
          if (location === "Overlay Color") {
            if (is3DOnly) {
              cdc.classList.add("hidden");
              cdc.querySelector("input").dataset.storedValue = cdc.querySelector("input").value;
              cdc.querySelector("input").value = "";
            } else {
              cdc.classList.remove("hidden");
              cdc.querySelector("input").value = cdc.querySelector("input").dataset.storedValue;
            }
            return;
          }
          
          if (location === "Inner Outline Color") {
            if (isSingleOutlineNotDouble){
              cdc.classList.remove("hidden");
              cdc.querySelector("input").value = cdc.querySelector("input").dataset.storedValue
              cdc.querySelector("p").innerHTML = "Outline Color";
            } else if (isSolid) {
              cdc.classList.add("hidden");
              cdc.querySelector("input").dataset.storedValue = cdc.querySelector("input").value
            } else {
              cdc.classList.remove("hidden");
              cdc.querySelector("input").value = ""
              cdc.querySelector("p").innerHTML = "Inner Outline Color";
            }
            return;
          }

          if (location === "Outer Outline Color") {
            if (isSingleOutlineNotDouble){
              cdc.classList.add("hidden");
              cdc.querySelector("input").dataset.storedValue = cdc.querySelector("input").value
            } else if (isSolid) {
              cdc.classList.add("hidden");
              cdc.querySelector("input").dataset.storedValue = cdc.querySelector("input").value
            } else {
              cdc.classList.remove("hidden");
              cdc.querySelector("input").value = cdc.querySelector("input").dataset.storedValue
            }
            return;
          }
          

          // All other locations
          if (isSolid || is3DOnly) {
            cdc.classList.add("hidden");
            cdc.querySelector("input").dataset.storedValue = cdc.querySelector("input").value;
            cdc.querySelector("input").value = "";
          } else {
            cdc.classList.remove("hidden");
            cdc.querySelector("input").value = cdc.querySelector("input").dataset.storedValue;
          }
        });
      }
    }
  );
}
*/